import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../../axiosInstance";
import getDecryptData from "../../../helpers/getDecryptData";

const DEFAULT_FOOTER_GROUPS = [
  {
    title: "Quick Links",
    icon: "ri-link",
    links: [
      { text: "Explore", link: "/" },
      { text: "Browse Jobs", link: "/jobs" },
      { text: "About Us", link: "/about-us" },
      { text: "Contact", link: "/contact-us" },
    ],
  },
  {
    title: "For Employers",
    icon: "ri-briefcase-line",
    links: [
      { text: "Post a Job", link: "/login" },
      { text: "Talent Solutions", link: "/jobs" },
      { text: "Applicant Tracking", link: "/login" },
      { text: "Pipeline Management", link: "/login" },
    ],
  },
];

let cachedHomeData = null;
let cachedSession = null;
let hasSessionCache = false;

const normalizeLink = (value) => {
  if (!value || typeof value !== "string") return "/";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/")) return value;
  return `/${value}`;
};

const flattenLinks = (items = []) => {
  if (!Array.isArray(items)) return [];

  const result = [];

  const walk = (entryList) => {
    entryList.forEach((entry) => {
      if (entry?.text && entry?.link) {
        result.push({
          text: String(entry.text),
          link: normalizeLink(entry.link),
          target: entry.target || "_self",
        });
      }

      if (Array.isArray(entry?.children) && entry.children.length) {
        walk(entry.children);
      }
    });
  };

  walk(items);
  return result;
};

const buildFooterGroups = (items = []) => {
  if (!Array.isArray(items) || !items.length) return DEFAULT_FOOTER_GROUPS;

  const groups = items
    .filter((item) => item?.text)
    .map((item, index) => {
      const children = Array.isArray(item.children) ? flattenLinks(item.children) : [];
      const ownLink = item.link
        ? [{ text: item.text, link: normalizeLink(item.link), target: item.target || "_self" }]
        : [];
      const links = children.length ? children : ownLink;

      return {
        title: item.text,
        icon: index === 0 ? "ri-link" : "ri-briefcase-line",
        links,
      };
    })
    .filter((group) => group.links.length);

  if (groups.length) return groups;

  const flatLinks = flattenLinks(items);
  return flatLinks.length
    ? [{ ...DEFAULT_FOOTER_GROUPS[0], links: flatLinks }]
    : DEFAULT_FOOTER_GROUPS;
};

const FooterMenuLink = ({ item }) => {
  const isExternal = item.link.startsWith("http://") || item.link.startsWith("https://");

  return (
    <li>
      {isExternal ? (
        <a
          href={item.link}
          target={item.target || "_self"}
          rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
        >
          {item.text}
        </a>
      ) : (
        <Link to={item.link}>{item.text}</Link>
      )}
    </li>
  );
};

const normalizeAssetUrl = (value) => {
  if (!value) return "";
  if (String(value).startsWith("http://") || String(value).startsWith("https://")) return value;

  const base = String(axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
  const clean = String(value).replace(/^\/+/, "");

  return base ? `${base}/${clean}` : `/${clean}`;
};

const Footer = () => {
  const [homeData, setHomeData] = useState(null);
  const [session, setSession] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (cachedHomeData) {
        setHomeData(cachedHomeData);
      }
      if (hasSessionCache) {
        setSession(cachedSession);
      }
      if (cachedHomeData && hasSessionCache) {
        return;
      }

      try {
        const requests = [];

        if (!cachedHomeData) {
          requests.push(axiosInstance.get("/home-data"));
        } else {
          requests.push(Promise.resolve(null));
        }

        if (!hasSessionCache) {
          requests.push(getDecryptData());
        } else {
          requests.push(Promise.resolve(cachedSession));
        }

        const [homeResponse, user] = await Promise.all(requests);

        if (!cachedHomeData) {
          cachedHomeData = homeResponse?.data?.homeData || null;
          setHomeData(cachedHomeData);
        }

        if (!hasSessionCache) {
          cachedSession = user || null;
          hasSessionCache = true;
          setSession(cachedSession);
        }
      } catch (error) {
        if (!hasSessionCache) {
          cachedSession = null;
          hasSessionCache = true;
          setSession(null);
        }
      }
    };

    load();
  }, []);

  useEffect(() => {
    const syncSession = async (event) => {
      const nextSession =
        event?.detail !== undefined ? event.detail || null : await getDecryptData();
      cachedSession = nextSession;
      hasSessionCache = true;
      setSession(nextSession);
    };

    window.addEventListener("ynb-auth-changed", syncSession);
    return () => window.removeEventListener("ynb-auth-changed", syncSession);
  }, []);

  const dashboardLink =
    session?.type === 0
      ? "/dashboard"
      : session?.type === 2
      ? "/job-seeker/dashboard"
      : session?.type === 1 || session?.type === 3
      ? "/recruiter/dashboard"
      : "/login";

  const footerGroups = useMemo(() => buildFooterGroups(homeData?.footer), [homeData?.footer]);

  const logoUrl = normalizeAssetUrl(homeData?.meta_logo || "");

  return (
    <>
      <style>{`
        .portal-footer {
          background: #080d20;
          color: #fff;
          padding: 64px 0 28px;
          position: relative;
          overflow: hidden;
        }
        .portal-footer::before {
          content: "";
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 12% 10%, rgba(124, 58, 237, .16), transparent 30%),
            linear-gradient(180deg, rgba(255,255,255,.025), rgba(255,255,255,0));
          pointer-events: none;
        }
        .portal-footer .container {
          position: relative;
          z-index: 1;
        }
        .portal-footer-brand-row {
          max-width: 100%;
          margin-bottom: 34px;
        }
        .portal-footer-logo-card {
          display: inline-flex;
          align-items: center;
          gap: 18px;
          margin-bottom: 22px;
        }
        .portal-footer-logo-frame {
          width: 86px;
          height: 86px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          background: rgba(255,255,255,.06);
          border: 1px solid rgba(255,255,255,.12);
          overflow: hidden;
        }
        .portal-footer-logo-frame img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: #fff;
        }
        .portal-footer-brand-name {
          font-size: clamp(1.5rem, 3vw, 2.25rem);
          font-weight: 900;
          color: #fff;
          line-height: 1.1;
        }
        .portal-footer-brand-name span {
          color: #f97316;
        }
        .portal-footer-tagline {
          color: #d5d9e8;
          font-weight: 600;
          margin-top: 4px;
        }
        .portal-footer-description {
          color: #c3c8d8;
          font-size: 1.02rem;
          line-height: 1.75;
          margin: 0 0 24px;
        }
        .portal-footer-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px;
        }
        .portal-footer-card {
          min-height: 260px;
          padding: 24px;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,.12);
          background: rgba(255,255,255,.035);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.04);
        }
        .portal-footer-title {
          display: flex;
          align-items: center;
          gap: 16px;
          font-size: 1.05rem;
          font-weight: 900;
          margin-bottom: 22px;
          color: #fff;
        }
        .portal-footer-title-icon {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          background: linear-gradient(135deg, #8b5cf6, #5b21b6);
          box-shadow: 0 14px 28px rgba(91, 33, 182, .32);
          color: #fff;
          font-size: 21px;
        }
        .portal-footer-links {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .portal-footer-links li {
          margin-bottom: 0;
          border-bottom: 1px dashed rgba(255,255,255,.08);
        }
        .portal-footer-links li:last-child {
          border-bottom: 0;
        }
        .portal-footer-links a,
        .portal-footer-static-link {
          display: flex;
          align-items: center;
          gap: 12px;
          min-height: 44px;
          color: #e4e7f2;
          text-decoration: none;
          transition: 0.2s ease;
        }
        .portal-footer-links a::before,
        .portal-footer-static-link::before {
          content: "\\ea6e";
          font-family: remixicon;
          color: #8b5cf6;
          font-size: 20px;
        }
        .portal-footer-links a:hover,
        .portal-footer-static-link:hover {
          color: #fff;
          transform: translateX(3px);
        }
        .portal-muted {
          color: #c3c8d8;
        }
        .portal-footer-contact-list {
          display: grid;
          gap: 15px;
          color: #d5d9e8;
          font-size: 1rem;
          line-height: 1.55;
        }
        .portal-footer-contact-list span {
          display: grid;
          grid-template-columns: 24px 1fr;
          gap: 12px;
          align-items: start;
        }
        .portal-footer-contact-list i {
          color: #8b5cf6;
          font-size: 21px;
        }
        .portal-footer-socials {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }
        .portal-footer-socials a {
          width: 52px;
          height: 52px;
          border-radius: 10px;
          display: grid;
          place-items: center;
          color: #a78bfa;
          border: 1px solid rgba(139, 92, 246, .45);
          background: rgba(139, 92, 246, .08);
          text-decoration: none;
          font-size: 25px;
        }
        .portal-footer-socials a:hover {
          color: #fff;
          background: #7c3aed;
        }
        .portal-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          min-height: 58px;
          padding: 12px 22px;
          background: linear-gradient(135deg, #8b5cf6, #6d28d9);
          color: #fff;
          border-radius: 10px;
          text-decoration: none;
          border: none;
          font-weight: 900;
          transition: 0.2s ease;
          box-shadow: 0 18px 34px rgba(109, 40, 217, .28);
        }
        .portal-btn-secondary:hover {
          transform: translateY(-1px);
          color: #fff;
        }
        .portal-footer-bottom {
          border-top: 1px solid rgba(255,255,255,0.1);
          color: #c3c8d8;
          font-size: 15px;
          text-align: center;
        }
        .portal-footer-bottom a {
          color: #fff;
          text-decoration: none;
        }
        .portal-footer-badges {
          display: flex;
          justify-content: center;
          gap: 28px;
          flex-wrap: wrap;
          margin-top: 18px;
          color: #f4f2ff;
          font-weight: 800;
        }
        .portal-footer-badges span {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .portal-footer-badges i {
          color: #8b5cf6;
          font-size: 24px;
        }
        .portal-whatsapp-float {
          width: 72px;
          height: 72px;
          position: fixed;
          bottom: 22px;
          right: 22px;
          z-index: 99;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: #20c767;
          color: #fff;
          box-shadow: 0 16px 34px rgba(32, 199, 103, .35);
          text-decoration: none;
          font-size: 42px;
        }
        @media (max-width: 1199px) {
          .portal-footer-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 767px) {
          .portal-footer-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 575px) {
          .portal-footer {
            padding: 48px 0 24px;
          }
          .portal-footer-card {
            padding: 24px;
            min-height: auto;
          }
          .portal-footer-logo-frame {
            width: 72px;
            height: 72px;
          }
          .portal-whatsapp-float {
            width: 60px;
            height: 60px;
            font-size: 34px;
          }
        }
      `}</style>

      {homeData?.whatsapp && (
        <a
          href={`https://wa.me/${homeData.whatsapp}`}
          target="_blank"
          rel="noopener noreferrer"
          className="portal-whatsapp-float"
        >
          <i className="ri-whatsapp-line"></i>
        </a>
      )}

      <footer className="portal-footer">
        <div className="container">
          <div className="portal-footer-brand-row">
            <div className="portal-footer-logo-card">
              <div className="portal-footer-logo-frame">
                {logoUrl ? (
                  <img src={logoUrl} alt={homeData?.meta_title || "gojobify"} />
                ) : (
                  <span className="portal-brand-mark">...</span>
                )}
              </div>
              <div>
                <div className="portal-footer-brand-name">{homeData?.meta_title || "gojobify"}</div>
                <div className="portal-footer-tagline">Bridging Employers and Talent</div>
              </div>
            </div>
            <p className="portal-footer-description">
              {homeData?.meta_description ||
                "Whether you are actively applying or planning your next move, build momentum with a smarter, cleaner hiring experience."}
            </p>
            <div className="portal-footer-socials">
              {homeData?.social_links?.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name || "Social link"}
                >
                  <i className={`ri ${social.icon}`}></i>
                </a>
              ))}
            </div>
          </div>

          <div className="portal-footer-grid">
            {footerGroups.slice(0, 2).map((group) => (
              <div className="portal-footer-card" key={group.title}>
                <div className="portal-footer-title">
                  <span className="portal-footer-title-icon"><i className={group.icon}></i></span>
                  {group.title}
                </div>
                <ul className="portal-footer-links">
                  {group.links.slice(0, 8).map((item) => (
                    <FooterMenuLink item={item} key={`${group.title}-${item.link}-${item.text}`} />
                  ))}
                </ul>
              </div>
            ))}

            <div className="portal-footer-card">
              <div className="portal-footer-title">
                <span className="portal-footer-title-icon"><i className="ri-mail-line"></i></span>
                Contact Us
              </div>
              <div className="portal-footer-contact-list">
                <span><i className="ri-mail-line"></i>{homeData?.email || "gojobify@gmail.com"}</span>
                {homeData?.email2 && <span><i className="ri-mail-line"></i>{homeData.email2}</span>}
                <span><i className="ri-phone-line"></i>{homeData?.phone || "+91 00000 00000"}</span>
                {homeData?.phone2 && <span><i className="ri-phone-line"></i>{homeData.phone2}</span>}
                <span><i className="ri-map-pin-line"></i>{homeData?.address || "India"}</span>
              </div>
            </div>

            <div className="portal-footer-card">
              <div className="portal-footer-title">
                <span className="portal-footer-title-icon"><i className="ri-group-line"></i></span>
                Follow Us
              </div>
              <p className="portal-muted mb-4">
                Stay connected with us on social media for the latest updates and opportunities.
              </p>
              <div className="portal-footer-socials mb-4">
                {homeData?.social_links?.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name || "Social link"}
                  >
                    <i className={`ri ${social.icon}`}></i>
                  </a>
                ))}
              </div>
              <Link to={dashboardLink} className="portal-btn-secondary w-100">
                <i className="ri-handshake-line"></i> Partner With Us
              </Link>
            </div>
          </div>

          <div className="portal-footer-bottom mt-4 pt-4">
            <div>
              &copy; {new Date().getFullYear()} <strong>{homeData?.meta_title || "gojobify"}</strong>. All Rights Reserved. | Designed by <a href="https://seotowebdesign.com/"> Seo To Webdesign </a>
            </div>
            <div className="portal-footer-badges">
              <span><i className="ri-shield-check-fill"></i> Reliable Hiring</span>
              <span><i className="ri-briefcase-4-fill"></i> Better Careers</span>
              <span><i className="ri-line-chart-fill"></i> Faster Growth</span>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
